/**
 * learnerProfile.js — Feb 2026 · Emergent Command · Defensibility.
 * ──────────────────────────────────────────────────────────────
 * Derives a compact, non-PII learner profile from data the app
 * ALREADY holds (masteryEngine + profileStore + streak counter).
 *
 * Returned shape:
 *   {
 *     ageTier:        "seedling" | "explorer" | "pioneer",
 *     tier:           string,             // legacy tier (little/big/older)
 *     strandMastery:  { reading, arithmetic, applied },
 *     topStruggle:    string | null,      // strand id with lowest mastery
 *     recentStruggles:string[],           // up to 4 skill ids most recently missed
 *     streak:         number,
 *     dailyMinutes:   number,
 *     preferred:      "read" | "watch" | "play" | null,
 *     theme:          string | null,      // Treasure Quest / Explorer / …
 *     dueSkills:      string[],           // skills whose review is overdue
 *   }
 *
 * STRICT RULE — never returns name, birth country, email, or any
 * text a child has typed. Only mastery/behaviour signals.
 */
import { getActiveProfile } from "@/lib/profileStore";
import { strandMastery } from "@/lib/masteryEngine";

const LS_MASTERY_KEY = "we_mastery_progress_v1";
function readMasteryState() {
  try { return JSON.parse(localStorage.getItem(LS_MASTERY_KEY) || "{}"); }
  catch { return {}; }
}

/** Best-effort read of the localStorage-persisted streak. */
function readStreak() {
  try {
    const v = parseInt(localStorage.getItem("mwq_daily_streak") || "0", 10);
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}

/** Best-effort read of the preferred interaction type. Heuristic: which
 *  activity kind has been completed most in the last 14 days? */
function readPreferred() {
  try {
    const raw = localStorage.getItem("we_activity_kinds_v1") || "{}";
    const map = JSON.parse(raw);
    const entries = Object.entries(map || {});
    if (!entries.length) return null;
    entries.sort((a, b) => (b[1] || 0) - (a[1] || 0));
    const winner = entries[0][0];
    return ["read", "watch", "play"].includes(winner) ? winner : null;
  } catch { return null; }
}

function readTheme() {
  try { return localStorage.getItem("mwq_theme_v1") || null; } catch { return null; }
}

/** Compact struggle list — the last 4 skills the child got wrong. Reads
 *  from the masteryEngine internal state (already keyed by skill). */
function readRecentStruggles(state) {
  try {
    const items = Object.entries(state?.skills || {})
      .filter(([, v]) => (v?.masteryScore ?? 1) < 0.5)
      .sort(([, a], [, b]) => (b?.lastSeenAt || 0) - (a?.lastSeenAt || 0))
      .slice(0, 4)
      .map(([k]) => k);
    return items;
  } catch { return []; }
}

function readDueSkills(state) {
  try {
    const now = Date.now();
    return Object.entries(state?.skills || {})
      .filter(([, v]) => (v?.reviewDueAt || 0) > 0 && (v?.reviewDueAt || 0) <= now)
      .map(([k]) => k)
      .slice(0, 8);
  } catch { return []; }
}

/** Public — call anywhere. Never throws. */
export function deriveLearnerProfile() {
  const profile = getActiveProfile();
  const state = readMasteryState();

  const strands = ["reading-writing", "arithmetic", "applied"];
  const strandScores = {};
  strands.forEach((s) => {
    try { strandScores[s] = Number(strandMastery(s) ?? 0); }
    catch { strandScores[s] = 0; }
  });
  // Lowest-scored strand is the "topStruggle" — the strand we'd nudge
  // the child toward.
  const topStruggle = strands.reduce(
    (min, s) => (strandScores[s] < (strandScores[min] ?? 1) ? s : min),
    strands[0],
  );

  return {
    ageTier: profile.ageTier || profile.tier || "explorer",
    tier: profile.tier || "explorer",
    strandMastery: strandScores,
    topStruggle,
    recentStruggles: readRecentStruggles(state),
    streak: readStreak(),
    dailyMinutes: Number(profile.dailyMinutes || 0),
    preferred: readPreferred(),
    theme: readTheme(),
    dueSkills: readDueSkills(state),
    // Non-PII marker for logs — a stable but anonymous cohort key.
    cohort: profile.ageTier || profile.tier || "explorer",
  };
}
